import { Refresh } from 'iconsax-react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'

import { EmptyState } from '@/components/EmptyState/EmptyState'
import { LottieSpinner } from '@/components/LottieSpinner/LottieSpinner'
import { Input } from '@/components/ui/input'
import { useAutoFillPages } from '@/hooks/useAutoFillPages'
import type { IExpiredReportsCache } from '@/store/api/Enterprise/Enterprise.types'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'

import { ExpiredSocialAnalysisItem } from './ExpiredSocialAnalysisItem'
import { ReportsFilterSheet } from './ReportsFilterSheet'

interface ExpiredReportsTabProps {
  data: IExpiredReportsCache | undefined
  isFetching: boolean
  loadNextPage: () => void

  selectedOfficeAndTeam: { workspace: string | null; team: string | null }
  onApplyFilters: (filters: {
    workspace: string | null
    team: string | null
  }) => void

  search: string
  onSearchChange: (value: string) => void

  showAllOfficesOption: boolean

  autoFetchThreshold: number
}

export const ExpiredReportsTab = ({
  data,
  isFetching,
  loadNextPage,
  selectedOfficeAndTeam,
  onApplyFilters,
  search,
  onSearchChange,
  showAllOfficesOption,
  autoFetchThreshold,
}: ExpiredReportsTabProps) => {
  const { t } = useTranslation()

  const reports = data?.list ?? []
  const totalItems = data?.totalItems ?? 0
  const reportRetentionDays = data?.reportRetentionDays ?? null
  const hasMore = reports.length < totalItems

  useAutoFillPages({
    itemCount: reports.length,
    threshold: autoFetchThreshold,
    hasMore,
    isFetching,
    loadNextPage,
    resetKey: `${search}|${selectedOfficeAndTeam.workspace}|${selectedOfficeAndTeam.team}`,
  })

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-5">
      <div className="w-full flex sm:items-center max-sm:flex-col gap-6 mx-3">
        <Input
          placeholder={t('dashboard.reportsScreen.search')}
          className="flex h-9 w-[320px] rounded-lg max-sm:w-full"
          value={search}
          useDebounce={true}
          debounceTime={600}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <ReportsFilterSheet
          selectedOfficeAndTeam={selectedOfficeAndTeam}
          onApplyFilters={onApplyFilters}
          showReportTypeFilter={false}
          showStatusFilter={false}
          showAllOfficesOption={showAllOfficesOption}
        />
      </div>

      {reportRetentionDays !== null && (
        <div className="flex items-center gap-2 mx-3">
          <Refresh
            size={16}
            color={weCheckPalette.dynamicBlue}
            className="shrink-0"
          />
          <p className="text-sm font-normal text-dynamicBlue">
            {t('dashboard.reportsScreen.expiredReports.disclaimer', {
              days: reportRetentionDays,
            })}
          </p>
        </div>
      )}

      <div
        id="expiredReportsScrollable"
        className="flex-1 min-h-0 overflow-y-auto custom-scrollbar"
      >
        {isFetching && !data ? (
          <div className="flex items-center justify-center h-[300px]">
            <LottieSpinner />
          </div>
        ) : reports.length === 0 ? (
          <EmptyState
            text={
              search === ''
                ? t('dashboard.reportsScreen.expiredReports.emptyState')
                : `${t('dashboard.reportsScreen.noResultsFor')} "${search}"`
            }
          />
        ) : (
          <InfiniteScroll
            className="flex flex-col"
            dataLength={reports.length}
            next={loadNextPage}
            hasMore={hasMore}
            scrollableTarget="expiredReportsScrollable"
            loader={
              <p className="w-full text-center text-neutralColor60 font-semibold py-3">
                {t('dashboard.reportsScreen.loading')}
              </p>
            }
            endMessage={
              reports.length === 0 && (
                <p className="w-full text-center text-neutralColor60 font-semibold py-3">
                  {t('dashboard.reportsScreen.endMessage')}
                </p>
              )
            }
          >
            {reports.map((report, index) => (
              <ExpiredSocialAnalysisItem key={index} {...report} />
            ))}
          </InfiniteScroll>
        )}
      </div>
    </div>
  )
}
